import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { ResolvedContentReference } from '@gitbook/app-database';
import { useHiveBranch, useHiveClient, useHiveDataSyncClock } from '@gitbook/hive-react';
import { Icon } from '@gitbook/spine-icons';
import type { IconDefinition } from '@gitbook/spine-react';
import { useEventCallback, useLiveRef, usePrevious } from '@gitbook/util-react-hooks';
import { executeSuspenseAsync } from '@gitbook/util-suspense-cache';
import { assert } from '@gitbook/util-ts';

import { useNavigation } from '../../navigation';
import { FileIcon } from '../Files';
import { PageIcon } from '../Page';
import { SpaceIcon } from '../Space';
import { UserAvatar } from '../Users';
import { type ResolutionOptions, resolveContentRefCache } from './caches';
import { useContentReferenceContext } from './context';
import type { UIResolvedContentReference } from './types';
import { useGitPageURLReferenceReader, useGitPageURLReferences } from './useGitPageURLReferences';

/**
 * Resolve content references to a metadata to render in the UI.
 *
 * It relies on a ContentRefContext being provided at a higher level via a React context.
 */
export function useContentReferences(
    contentRefs: api.ContentRef[],
    options?: ResolutionOptions
): UIResolvedContentReference[] {
    const hiveClient = useHiveClient();
    const context = useContentReferenceContext();
    const effectiveRefs = useGitPageURLReferences(contentRefs, context);
    const resolutionInputs = effectiveRefs.map(
        ({ ref, context: targetContext }) =>
            [hiveClient, ref, targetContext, options ?? {}] as const
    );

    const allResolved = resolveContentRefCache.useValues(resolutionInputs);

    // Use the HiveBranch clock as a cache key, to ensure we invalidate
    // the cache when the revision mutates.
    const hiveBranch = useHiveBranch();
    const hiveBranchClock = useHiveDataSyncClock(hiveBranch?.dataSync);

    const cacheKey = `${hiveBranchClock ?? ''}`;
    const previousCacheKey = usePrevious(cacheKey);

    // Refetch the content refererences when the cache key changes
    const resolutionInputsRef = useLiveRef(resolutionInputs);
    React.useEffect(() => {
        if (previousCacheKey !== undefined && previousCacheKey !== cacheKey) {
            const inputs = resolutionInputsRef.current;

            // Refresh the cache for all the inputs
            inputs.forEach((input) => {
                resolveContentRefCache.fetch(input);
            });
        }
    }, [cacheKey, previousCacheKey, resolutionInputsRef]);

    const toUIResolved = useResolvedToUIContentRef();

    return React.useMemo(() => {
        return allResolved.map(([resolved, permissionsDenied], index) => {
            const contentRef = effectiveRefs[index]?.ref;
            assert(contentRef);
            return toUIResolved({ resolved, permissionsDenied, contentRef });
        });
    }, [toUIResolved, effectiveRefs, allResolved]);
}

/**
 * Return a function to convert a resolved content ref into a UI content ref.
 */
function useResolvedToUIContentRef() {
    const { getHref, navigate } = useResolvedContentRefNavigation();
    return useEventCallback(
        (args: {
            resolved: ResolvedContentReference<api.Space> | null;
            permissionsDenied: boolean;
            contentRef: api.ContentRef;
        }): UIResolvedContentReference => {
            const { resolved, permissionsDenied, contentRef } = args;

            if (!resolved) {
                return {
                    title: permissionsDenied ? 'Access denied' : 'Broken link',
                    icon: Icon.LinkBroken,
                    secondaryIcon: Icon.LinkBroken,
                    href: '',
                    open: () => {},
                };
            }

            const href = getHref({ resolved, contentRef });
            return {
                title: resolved.text,
                icon: createIconForContentRef(contentRef, resolved),
                secondaryIcon: createSecondaryIconForContentRef(contentRef),
                secondaryIconDescription: createIconDescriptionContentRef(contentRef),
                href: href ?? '',
                open: () => navigate({ resolved, contentRef }),
                resolution: resolved,
            };
        }
    );
}

/**
 * Check if the content ref can be linked.
 */
export function checkIsLinkableContentRef(contentRef: api.ContentRef) {
    return contentRef.kind !== 'user';
}

/**
 * Return navigation functions for a resolved content ref.
 */
function useResolvedContentRefNavigation() {
    const { getNavigationURL, navigate: navigateToScreen } = useNavigation();
    const getHref = useEventCallback(
        (args: { resolved: ResolvedContentReference<api.Space>; contentRef: api.ContentRef }) => {
            const { resolved, contentRef } = args;
            if (!checkIsLinkableContentRef(contentRef)) {
                return null;
            }
            if (typeof resolved.target === 'string') {
                return resolved.target;
            }
            return getNavigationURL({ screen: resolved.target });
        }
    );
    const navigate = useEventCallback(
        (args: { resolved: ResolvedContentReference<api.Space>; contentRef: api.ContentRef }) => {
            const { resolved, contentRef } = args;
            if (!checkIsLinkableContentRef(contentRef)) {
                return;
            }

            if (typeof resolved.target === 'string') {
                window.open(resolved.target, '_blank');
                return;
            }

            navigateToScreen({ screen: resolved.target });
        }
    );
    return { getHref, navigate };
}

/**
 * Clears the content reference cache for the current context.
 */
export function useClearContentReferenceCache(options?: ResolutionOptions) {
    const hiveClient = useHiveClient();
    const context = useContentReferenceContext();
    return useEventCallback((contentRef: api.ContentRef) => {
        resolveContentRefCache.clear([hiveClient, contentRef, context, options]);
    });
}

/**
 * Returns a callback to resolve a content reference using the current context.
 */
export function useResolveContentReference() {
    const hiveClient = useHiveClient();
    const context = useContentReferenceContext();
    const readGitPageURLReference = useGitPageURLReferenceReader(context);
    const toUIResolved = useResolvedToUIContentRef();

    return useEventCallback(async (contentRef: api.ContentRef, options?: ResolutionOptions) => {
        const effective = await executeSuspenseAsync(() => readGitPageURLReference(contentRef));
        const [resolved, permissionsDenied] = await resolveContentRefCache.readAsync([
            hiveClient,
            effective.ref,
            effective.context,
            options,
        ]);
        return toUIResolved({ resolved, permissionsDenied, contentRef: effective.ref });
    });
}

/**
 * Resolve a content reference to an UI representation.
 */
export function useContentReference(
    contentRef: api.ContentRef | undefined | null,
    options?: ResolutionOptions
): UIResolvedContentReference {
    const result = useContentReferences(contentRef ? [contentRef] : [], options);

    return (
        result[0] || {
            title: 'Broken link',
            icon: Icon.LinkBroken,
            secondaryIcon: Icon.LinkBroken,
            href: '',
            open: () => {},
        }
    );
}

/**
 * Return a reader that resolves a content reference to its UI representation.
 *
 * Unlike `useContentReference`, the suspension happens where the reader is called rather than in
 * the component holding the hook. Use it to read a reference from a palette group's `commands`,
 * so an unresolved reference suspends the palette instead of the component anchoring it.
 */
export function useContentReferenceReader(): (
    contentRef: api.ContentRef,
    options?: ResolutionOptions
) => UIResolvedContentReference {
    const hiveClient = useHiveClient();
    const context = useContentReferenceContext();
    const readGitPageURLReference = useGitPageURLReferenceReader(context);
    const toUIResolved = useResolvedToUIContentRef();

    return useEventCallback((contentRef, options) => {
        const effective = readGitPageURLReference(contentRef);
        const [resolved, permissionsDenied] = resolveContentRefCache.read([
            hiveClient,
            effective.ref,
            effective.context,
            options,
        ]);
        return toUIResolved({ resolved, permissionsDenied, contentRef: effective.ref });
    });
}

function createIconForContentRef(
    contentRef: api.ContentRef,
    resolved: ResolvedContentReference<api.Space>
): IconDefinition | undefined {
    if (contentRef.kind === 'url') {
        return Icon.Link;
    }

    if (typeof resolved.target !== 'string' && resolved.target.type === 'user') {
        return <UserAvatar user={resolved.target.user} />;
    }

    if (resolved.extra?.file) {
        return <FileIcon file={resolved.extra.file} />;
    }

    if (resolved.extra?.space) {
        return <SpaceIcon space={resolved.extra.space} />;
    }

    if (resolved.extra?.page && (resolved.extra.page.emoji || resolved.extra.page.icon)) {
        return <PageIcon page={resolved.extra.page} />;
    }

    return undefined;
}

function createSecondaryIconForContentRef(contentRef: api.ContentRef) {
    switch (contentRef.kind) {
        case 'user':
            return Icon.AtSign;
        case 'anchor':
            return Icon.Hash;
        case 'page':
            return Icon.PageLink;
        case 'space':
            return Icon.SpaceLink;
        case 'collection':
            return Icon.CollectionLink;
        case 'url':
            return Icon.Link;
    }
}

function createIconDescriptionContentRef(contentRef: api.ContentRef): string | undefined {
    switch (contentRef.kind) {
        case 'user':
            return 'User mention';
        case 'anchor':
            return `Link to a page's anchor`;
        case 'page':
            return 'Link to a page';
        case 'space':
            return 'Link to a space';
        case 'collection':
            return 'Link to a acollection';
        case 'url':
            return 'External URL';
    }
}
